/*
  hlth-pdp-formula.js — makes "Under 50 / Over 50" behave like a toggle.

  WHY THIS EXISTS. The two formulas are separate Shopify products, so the
  toggle has to be real links: that is what makes the right variant land in
  the cart, what makes the page shareable, and what works with JS off. But a
  full navigation doesn't FEEL like a toggle — you lose your scroll position
  and the page flashes, which reads as "I've been sent somewhere else"
  rather than "the thing in front of me changed".

  So the links stay links, and this upgrades them: fetch the other formula's
  page, swap <main>, and keep the toggle you clicked pinned to the exact same
  spot on screen. Anything unexpected — a failed fetch, a page we can't parse,
  a modified click — falls straight back to the ordinary navigation the markup
  already describes. Nothing here is load-bearing.
*/
(function () {
  'use strict';

  if (window.__hlthFormulaSwap) return;
  window.__hlthFormulaSwap = true;

  var SELECTOR = 'nav[aria-label="Choose your formula"]';

  if (!window.fetch || !window.history || !history.pushState || !window.DOMParser) return;

  var main = document.querySelector('main');
  if (!main) return;

  var busy = false;

  document.addEventListener('click', function (e) {
    // Let the browser handle anything that isn't a plain left click: cmd-click
    // to open a new tab must keep working.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.target.closest ? e.target.closest(SELECTOR + ' a[href]') : null;
    if (!link) return;

    // The pill for the formula you're already on is a no-op, not a reload.
    if (link.hasAttribute('aria-current')) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    swap(link);
  });

  function targetUrl(link) {
    var url = new URL(link.href, location.href);
    // Carry ?view= across. Without this, previewing an unassigned template
    // toggles you off it and onto whatever template the product normally uses.
    var view = new URLSearchParams(location.search).get('view');
    if (view && !url.searchParams.has('view')) url.searchParams.set('view', view);
    return url.toString();
  }

  function swap(link) {
    if (busy) return;
    busy = true;

    var url = targetUrl(link);

    // Pin the clicked toggle rather than restoring a raw scrollY. The two
    // formulas have different copy, so the page above the toggle can change
    // height; keeping the pixel offset would still drift. Keeping the CONTROL
    // still is what makes this read as a toggle.
    var anchorOffset = link.getBoundingClientRect().top;
    var anchorSection = link.closest('section[id]');
    var anchorId = anchorSection ? anchorSection.id : null;
    var pillText = (link.textContent || '').trim();

    main.setAttribute('aria-busy', 'true');

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.querySelector('main');
        if (!next || !next.children.length) throw new Error('no main');

        main.replaceChildren.apply(main, Array.prototype.slice.call(next.childNodes));
        runScripts(main);

        if (doc.title) document.title = doc.title;
        history.pushState({ hlthFormula: true }, '', url);

        restorePosition(anchorId, pillText, anchorOffset);
        main.removeAttribute('aria-busy');
        busy = false;
      })
      .catch(function () {
        // Whatever went wrong, the link still works. Use it.
        location.href = url;
      });
  }

  function restorePosition(anchorId, pillText, anchorOffset) {
    var nav = null;
    var scope = anchorId ? document.getElementById(anchorId) : null;
    if (scope) nav = scope.querySelector(SELECTOR);
    if (!nav) nav = document.querySelector(SELECTOR);
    if (!nav) return;

    // The pill that was clicked is the one that is now current.
    var pill = nav.querySelector('a[aria-current]') || nav.querySelector('a');
    if (!pill) return;

    var se = document.scrollingElement || document.documentElement;
    var behavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto'; // the theme sets smooth; don't animate a restore
    se.scrollTop = se.scrollTop + pill.getBoundingClientRect().top - anchorOffset;
    document.documentElement.style.scrollBehavior = behavior;

    // Keyboard users must not be dumped at the top of the document.
    if (document.activeElement === document.body || !document.contains(document.activeElement)) {
      pill.focus({ preventScroll: true });
    }
  }

  /*
    innerHTML-inserted <script> never runs. The PDP sections carry their own
    inline behaviour (cycle toggles, the review carousel, the nutrition modal),
    so without this the swapped-in page is inert.
  */
  function runScripts(root) {
    root.querySelectorAll('script').forEach(function (old) {
      var s = document.createElement('script');
      for (var i = 0; i < old.attributes.length; i++) {
        s.setAttribute(old.attributes[i].name, old.attributes[i].value);
      }
      s.textContent = old.textContent;
      old.parentNode.replaceChild(s, old);
    });
  }

  // Back/forward across a swap: a reload is correct and cheap, and avoids
  // keeping a cache of previous documents in memory.
  window.addEventListener('popstate', function (e) {
    if (e.state && e.state.hlthFormula) location.reload();
  });
})();
